// react
import { useContext, useMemo, useState } from 'react';

// types
import {
	ActionButton,
	Entity,
	Ifr,
	Pagination,
	Transaction,
	TransactionAugmented,
} from '../../../../@types';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardTransaction } from '../../../cards/CardTransaction/CardTransaction';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// utils
import { translateType } from '../../../../utils/translations';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableOpExProps {
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TableOpEx = ({
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TableOpExProps) => {
	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const { idEntity } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCategory = searchParams.get('category');
	const paramComponent = searchParams.get('component');
	const paramEntity = searchParams.get('entity');
	const paramExpenseType = searchParams.get('expenseType');
	const paramFromEntity = searchParams.get('fromEntity');
	const paramQ = searchParams.get('q');
	const paramTags = searchParams.get('tags');

	// defaults
	const defaultFiltersValues = {
		category: 'all',
		component: 'all',
		expenseType: 'all',
		fromEntity: 'all',
		tags: [],
	};

	// state
	const [entities, setEntities] = useState<Entity[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		category: paramCategory || defaultFiltersValues.category,
		component: paramComponent || defaultFiltersValues.component,
		expenseType: paramExpenseType || defaultFiltersValues.expenseType,
		fromEntity: paramFromEntity || defaultFiltersValues.fromEntity,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
	});
	const [ifrs, setIfrs] = useState<Ifr[]>([]);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch entities
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch ifrs
	useFetch({
		isEnabled: Boolean(project?.id && featureFlags['IFRS']),
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&status=!DELETED&fields=currency,dateEnd,dateStart,id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setIfrs(res.data);
		},
	});

	// vars
	const idFormFilters = 'form-opex-filters';

	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 1.5,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('classification'),
			value: 'expenseType',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('entity'),
			value: 'entity',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('traceability'),
			value: 'traceability',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('account'),
			value: 'account',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'modified',
		},
	];

	// options
	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			secondary: translateType(entity.type, t),
			value: String(entity.id),
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	if (userProjectPermissions?.entity?.read && !idEntity) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-entity`,
			label: t('entity'),
			name: 'fromEntity',
			onChange: handleChangeFilterValue,
			options: optionsEntities,
			type: 'SELECT',
			value: filtersValues.fromEntity,
		});
	}

	if (userProjectPermissions?.component?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-component`,
			label: t('component'),
			name: 'component',
			onChange: handleChangeFilterValue,
			type: 'COMPONENT',
			value: filtersValues.component,
		});
	}

	if (userProjectPermissions?.category?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-category`,
			label: t('category'),
			name: 'category',
			onChange: handleChangeFilterValue,
			type: 'CATEGORY',
			value: filtersValues.category,
		});
	}

	if (userProjectPermissions?.expenseType?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-expense-type`,
			label: t('expenseType'),
			name: 'expenseType',
			onChange: handleChangeFilterValue,
			type: 'EXPENSE_TYPE',
			value: filtersValues.expenseType,
		});
	}

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.opex.content') },
			IconCircleProps: {
				color: 'grey',
				type: 'opex',
			},
			title: { children: t('empty.opex.title') },
		};

		if (userProjectPermissions?.opEx?.create && emptyProps.actions && project?.version !== '2') {
			emptyProps.actions.push({
				children: t('recordOpEx'),
				onClick: () => {
					searchParams.set('dialog', 'recordOpEx');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramEntity || paramQ) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [
		paramEntity,
		paramQ,
		project?.version,
		searchParams,
		setSearchParams,
		t,
		userProjectPermissions,
	]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					// add ifrs ids to transaction
					const transactionAugmented: TransactionAugmented = {
						...transaction,
						ifrs: ifrs.filter(
							(ifr) => ifr.dateStart <= transaction.date && ifr.dateEnd >= transaction.date
						),
					};

					return (
						<Grid item key={`entity-${i}`} xs={12}>
							<CardTransaction
								refetchTransactions={refetchTransactions}
								transaction={transactionAugmented}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, ifrs, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="opex" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={4} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnExport resource="OPEX" />
					</Grid>
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
